import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, AlertTriangle, Package, History, ArrowDown, ArrowUp, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";

const EMPTY = {
  name: "", category: "I përgjithshëm", unit: "copë",
  stock_qty: 0, min_qty: 5, unit_price: 0, supplier: "", notes: "",
};

export default function Inventory() {
  const { user } = useAuth();
  const { format, symbol } = useCurrency();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all"); // all | low | category
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // material or null
  const [moveOpen, setMoveOpen] = useState(null); // material
  const [historyOpen, setHistoryOpen] = useState(null); // material
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [moveForm, setMoveForm] = useState({ type: "in", qty: 0, reason: "" });

  const canEdit = ["admin", "doctor", "receptionist"].includes(user?.role);
  const canDelete = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/materials");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const categories = Array.from(new Set(list.map((m) => m.category))).sort();
  const filtered = filter === "all" ? list
    : filter === "low" ? list.filter((m) => m.stock_qty <= m.min_qty)
    : list.filter((m) => m.category === filter);
  const lowCount = list.filter((m) => m.stock_qty <= m.min_qty).length;
  const totalValue = list.reduce((s, m) => s + (m.stock_qty || 0) * (m.unit_price || 0), 0);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, category: m.category, unit: m.unit,
              stock_qty: m.stock_qty, min_qty: m.min_qty, unit_price: m.unit_price,
              supplier: m.supplier, notes: m.notes });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/materials/${editing.id}`, form);
        toast.success("Materiali u përditësua");
      } else {
        await api.post("/materials", form);
        toast.success("Materiali u shtua");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë material?")) return;
    try { await api.delete(`/materials/${id}`); toast.success("U fshi"); load(); }
    catch { toast.error("Nuk keni leje"); }
  };

  const submitMove = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/materials/${moveOpen.id}/movement`, {
        type: moveForm.type,
        qty: Number(moveForm.qty),
        reason: moveForm.reason,
      });
      toast.success("Lëvizja u regjistrua");
      setMoveOpen(null);
      setMoveForm({ type: "in", qty: 0, reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openHistory = async (m) => {
    setHistoryOpen(m);
    const { data } = await api.get(`/materials/${m.id}/movements`);
    setHistory(data);
  };

  return (
    <div className="space-y-8" data-testid="inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Magazina</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Inventari</h1>
          <p className="mt-2 text-sm text-[#5C5C5C]">Menaxhoni materialet klinike, stoqet dhe lëvizjet.</p>
        </div>
        {canEdit && (
          <button onClick={openCreate} className="btn-primary" data-testid="add-material-button">
            <Plus size={16} /> Material i Ri
          </button>
        )}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="surface-card p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EAE4D9] text-[#2C4C3B]">
              <Package size={18} />
            </div>
            <div>
              <div className="label-eyebrow">Total Artikuj</div>
              <div className="mt-1 text-2xl font-semibold tracking-tight">{list.length}</div>
            </div>
          </div>
        </div>
        <div className="surface-card p-6">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${lowCount > 0 ? "bg-[#9E3C3C]/15 text-[#9E3C3C]" : "bg-[#4A8B71]/15 text-[#2C4C3B]"}`}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <div className="label-eyebrow">Stok i Ulët</div>
              <div className="mt-1 text-2xl font-semibold tracking-tight">{lowCount}</div>
            </div>
          </div>
        </div>
        <div className="surface-card p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EAE4D9] text-[#2C4C3B]">
              <Settings2 size={18} />
            </div>
            <div>
              <div className="label-eyebrow">Vlera e Stokut</div>
              <div className="mt-1 text-2xl font-semibold tracking-tight">{format(totalValue)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Chip label="Të gjitha" active={filter === "all"} onClick={() => setFilter("all")} />
        <Chip label={`Stok i ulët (${lowCount})`} active={filter === "low"} onClick={() => setFilter("low")} danger={lowCount > 0} />
        {categories.map((c) => (
          <Chip key={c} label={c} active={filter === c} onClick={() => setFilter(c)} />
        ))}
      </div>

      <div className="surface-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">Pa rezultate.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left">
              <tr>
                <th className="px-6 py-3 label-eyebrow">Emri</th>
                <th className="px-6 py-3 label-eyebrow">Kategoria</th>
                <th className="px-6 py-3 label-eyebrow text-right">Stok</th>
                <th className="px-6 py-3 label-eyebrow text-right">Minimum</th>
                <th className="px-6 py-3 label-eyebrow text-right">Çmimi</th>
                <th className="px-6 py-3 label-eyebrow">Furnizuesi</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const isLow = m.stock_qty <= m.min_qty;
                return (
                  <tr key={m.id} className="border-t border-[#E2DFD8]" data-testid={`material-${m.id}`}>
                    <td className="px-6 py-3">
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-[#5C5C5C]">{m.unit}</div>
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <span className="rounded-full bg-[#EAE4D9] px-3 py-1 text-xs font-medium text-[#2C4C3B]">
                        {m.category}
                      </span>
                    </td>
                    <td className={`px-6 py-3 text-right font-mono ${isLow ? "text-[#9E3C3C] font-semibold" : ""}`}>
                      {m.stock_qty}
                      {isLow && <AlertTriangle size={12} className="inline ml-1" />}
                    </td>
                    <td className="px-6 py-3 text-right font-mono text-[#5C5C5C]">{m.min_qty}</td>
                    <td className="px-6 py-3 text-right font-mono">{format(m.unit_price)}</td>
                    <td className="px-6 py-3 text-xs text-[#5C5C5C]">{m.supplier || "—"}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setMoveOpen(m)} className="p-1.5 rounded hover:bg-[#EAE4D9]" title="Lëvizje stoku" data-testid={`move-${m.id}`}>
                          <ArrowUp size={14} className="inline text-[#4A8B71]" />
                          <ArrowDown size={14} className="inline text-[#9E3C3C] -ml-1" />
                        </button>
                        <button onClick={() => openHistory(m)} className="p-1.5 rounded hover:bg-[#EAE4D9]" title="Historiku">
                          <History size={14} />
                        </button>
                        {canEdit && (
                          <button onClick={() => openEdit(m)} className="p-1.5 rounded hover:bg-[#EAE4D9]" title="Redakto">
                            <Settings2 size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => remove(m.id)} className="p-1.5 rounded hover:bg-[#9E3C3C]/10 text-[#9E3C3C]" title="Fshi">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Përditëso Material" : "Shto Material"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <Field label="Emri"><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="material-name" /></Field>
            <Field label="Kategoria"><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="material-category" /></Field>
            <Field label="Njësia"><Input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} placeholder="copë, ml, gr…" /></Field>
            <Field label={`Çmimi (${symbol})`}><Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({...form, unit_price: Number(e.target.value)})} /></Field>
            <Field label="Stoku Aktual"><Input type="number" step="0.5" value={form.stock_qty} onChange={(e) => setForm({...form, stock_qty: Number(e.target.value)})} data-testid="material-stock" /></Field>
            <Field label="Stoku Minimum"><Input type="number" step="0.5" value={form.min_qty} onChange={(e) => setForm({...form, min_qty: Number(e.target.value)})} /></Field>
            <div className="md:col-span-2"><Field label="Furnizuesi"><Input value={form.supplier} onChange={(e) => setForm({...form, supplier: e.target.value})} /></Field></div>
            <div className="md:col-span-2"><Field label="Shënime"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></Field></div>
            <DialogFooter className="md:col-span-2">
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
              <button type="submit" className="btn-primary" data-testid="material-save-button">{editing ? "Përditëso" : "Ruaj"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Movement dialog */}
      <Dialog open={!!moveOpen} onOpenChange={(o) => !o && setMoveOpen(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Lëvizje Stoku · {moveOpen?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitMove} className="space-y-4 mt-2">
            <div>
              <Label className="label-eyebrow">Lloji</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[
                  { v: "in", label: "Hyrje", color: "text-[#2C4C3B] border-[#2C4C3B]" },
                  { v: "out", label: "Dalje", color: "text-[#9E3C3C] border-[#9E3C3C]" },
                  { v: "adjust", label: "Korrigjim", color: "text-[#5C5C5C] border-[#E2DFD8]" },
                ].map((t) => (
                  <button key={t.v} type="button" onClick={() => setMoveForm({...moveForm, type: t.v})}
                          className={`rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all ${
                            moveForm.type === t.v ? t.color : "border-[#E2DFD8] text-[#5C5C5C]"
                          }`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <Field label={moveForm.type === "adjust" ? "Stoku i Ri" : "Sasia"}>
              <Input type="number" step="0.5" required value={moveForm.qty}
                     onChange={(e) => setMoveForm({...moveForm, qty: e.target.value})} data-testid="move-qty" />
            </Field>
            <Field label="Arsyeja (opsionale)">
              <Input value={moveForm.reason} onChange={(e) => setMoveForm({...moveForm, reason: e.target.value})}
                     placeholder="P.sh. Përdorur për pacientin X / Furnizim i ri" />
            </Field>
            <DialogFooter>
              <button type="button" className="btn-ghost" onClick={() => setMoveOpen(null)}>Anulo</button>
              <button type="submit" className="btn-primary" data-testid="move-save-button">Regjistro</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyOpen} onOpenChange={(o) => !o && setHistoryOpen(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>Historiku · {historyOpen?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto mt-2">
            {history.length === 0 ? (
              <div className="py-10 text-center text-sm text-[#5C5C5C]">Pa lëvizje stoku.</div>
            ) : (
              <ul className="divide-y divide-[#E2DFD8]">
                {history.map((h) => (
                  <li key={h.id} className="flex items-center justify-between py-3 px-1">
                    <div>
                      <div className="font-medium">
                        {h.type === "in" ? "Hyrje" : h.type === "out" ? "Dalje" : "Korrigjim"} · {h.qty} {historyOpen?.unit}
                      </div>
                      <div className="text-xs text-[#5C5C5C]">{h.reason || "—"} · {h.user_name}</div>
                    </div>
                    <div className="text-xs text-[#5C5C5C]">{new Date(h.created_at).toLocaleString("sq-AL")}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Chip({ label, active, onClick, danger }) {
  return (
    <button onClick={onClick}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              active
                ? (danger ? "bg-[#9E3C3C] text-white" : "bg-[#2C4C3B] text-[#FDFBF7]")
                : "bg-white border border-[#E2DFD8] text-[#5C5C5C] hover:text-[#2C4C3B]"
            }`}>
      {label}
    </button>
  );
}
